/**
 * Merge Tag Block for Editor.js
 * Easily insert personalization merge tags
 */

import { BlockTool } from '@editorjs/editorjs';

export interface MergeTagBlockData {
  tag: 'firstName' | 'lastName' | 'email';
}

const MERGE_TAGS = {
  firstName: {
    label: 'First Name',
    placeholder: '{{firstName}}',
    description: "Recipient's first name",
  },
  lastName: {
    label: 'Last Name',
    placeholder: '{{lastName}}',
    description: "Recipient's last name",
  },
  email: {
    label: 'Email',
    placeholder: '{{email}}',
    description: "Recipient's email address",
  },
};

export default class MergeTagBlock implements BlockTool {
  private data: MergeTagBlockData;
  private wrapper: HTMLElement | null = null;

  static get toolbox() {
    return {
      title: 'Merge Tag',
      icon: '<svg width="17" height="15" viewBox="0 0 17 15" xmlns="http://www.w3.org/2000/svg"><text x="2" y="12" font-size="12" font-weight="bold" fill="currentColor">{{}}</text></svg>',
    };
  }

  static get isInline() {
    return false;
  }

  constructor({ data }: { data: MergeTagBlockData }) {
    this.data = {
      tag: data.tag || 'firstName',
    };
  }

  render(): HTMLElement {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('email-merge-tag-block');
    this.updateWrapper();
    return this.wrapper;
  }

  private updateWrapper(): void {
    if (!this.wrapper) return;

    const tagInfo = MERGE_TAGS[this.data.tag];

    this.wrapper.style.cssText = `
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: linear-gradient(135deg, #f0f0f0 0%, #e5e5e5 100%);
      border: 1px solid #ddd;
      border-radius: 20px;
      margin: 8px 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    this.wrapper.innerHTML = `
      <span style="
        display: inline-block;
        padding: 2px 8px;
        background: #D4A657;
        color: white;
        font-size: 11px;
        font-weight: 600;
        border-radius: 10px;
        text-transform: uppercase;
      ">
        ${tagInfo.label}
      </span>
      <code style="
        font-family: 'SF Mono', Monaco, monospace;
        font-size: 14px;
        color: #722f37;
        font-weight: 500;
      ">
        ${tagInfo.placeholder}
      </code>
    `;
  }

  save(): MergeTagBlockData {
    return this.data;
  }

  renderSettings(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.padding = '8px';

    // Tag selector
    const label = document.createElement('label');
    label.textContent = 'Select Tag:';
    label.style.cssText = 'display: block; margin-bottom: 8px; font-size: 12px; color: #666; font-weight: 600;';

    const select = document.createElement('select');
    select.style.cssText = 'width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px;';

    Object.entries(MERGE_TAGS).forEach(([key, tag]) => {
      const option = document.createElement('option');
      option.value = key;
      option.textContent = `${tag.label} - ${tag.placeholder}`;
      option.selected = this.data.tag === key;
      select.appendChild(option);
    });

    select.addEventListener('change', () => {
      this.data.tag = select.value as MergeTagBlockData['tag'];
      this.updateWrapper();
    });

    // Description
    const description = document.createElement('p');
    description.style.cssText = 'margin: 12px 0 0; font-size: 12px; color: #999; line-height: 1.4;';
    description.textContent = 'This tag will be replaced with the actual value when the email is sent.';

    wrapper.appendChild(label);
    wrapper.appendChild(select);
    wrapper.appendChild(description);

    return wrapper;
  }
}
